import { FC, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_LOCATION, GET_ZONES } from "./requests";
import type { CascaderProps } from 'antd';
import {
  Form,
  Input,
  Button,
  message,
  Drawer,
  Row,
  Col,
  Card,
  Typography,
  Badge,
  Tooltip,
  Cascader,
  Image,
  Upload,
  Spin,
} from "antd";
import LocationSearchInput from "../../../templates/location-search-input";
import { formatAddress } from "../../../utils/functions";
import { QuestionCircleOutlined, UploadOutlined } from "@ant-design/icons";
import Map from "../../../templates/map";
import CustomMarker from "../../../templates/map/components/custom-marker";
import {
  Location,
  GMapsFormattedAddress,
  baseAddress,
} from "../../../types/location";
import { Option } from "../../../types/constants";
import UserSelectionForm from "../assignUsers";
import useUpload from "../../../hooks/use-upload";

const { Text, Title } = Typography;
const { TextArea } = Input;

interface CreateLocationFormProps {
  addingLocation: boolean;
  setAddingLocation: (value: boolean) => void;
  refetchLocations: () => void;
}

const CreateLocationForm: FC<CreateLocationFormProps> = ({
  addingLocation,
  setAddingLocation,
  refetchLocations,
}) => {
  const [form] = Form.useForm();
  const { upload } = useUpload();
  const [createLocation] = useMutation(CREATE_LOCATION);
  const [locationSearch, setLocationSearch] = useState("");
  const [subZone, setSubZone] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<Record<string, string[]>>({
    dropoff: [""],
    manager: [""],
    operation: [""],
  });
  const [imageUrl, setImageUrl] = useState(null);
  const [uploadLoding, setUploadLoading] = useState(false);

  const [destination, setDestination] =
    useState<GMapsFormattedAddress>(baseAddress);

  const { data } = useQuery(GET_ZONES, {
    variables: {
      params: { page: 1,
        pageSize: 20 },
    },
  });

  const onFinish = async (values: Location) => {
    try {
      const usersIds = Object.values(selectedUsers).flat();

      const response = await createLocation({
        variables: {
          location: {
            ...values,
            subZone,
            img: imageUrl,
          },
          users: usersIds,
        },
      });

      message.success(
        response.data?.createLocation?.message ||
          "Location created successfully!",
      );

      form.resetFields();
      await refetchLocations();
      setAddingLocation(false);
    } catch (err: any) {
      message.error(`Error: ${err.message}`);
    }
  };

  const onChange: CascaderProps<Option>['onChange'] = (value) => {
    if (value && value.length) {
      return setSubZone(value[value.length - 1]);
    }
    return setSubZone("");
  };

  const handleFileUpload = async ({ fileList }) => {
    if (fileList[0]) {
      const fileName = `locations/${new Date().toISOString()}/${
        fileList[0]?.name
      }`;
      setUploadLoading(true);
      const url = await upload(fileList[0].originFileObj, fileName);

      setImageUrl(url);
    }
    setUploadLoading(false);
    message.success("Imagen agregada.");
  };

  const onClosed = () => {
    setDestination(baseAddress);
    form.resetFields();
    setLocationSearch("");
    setAddingLocation(false);
    setSelectedUsers({
      dropoff: [""],
      manager: [""],
      operation: [""],
    })
    setImageUrl(null)
  };

  return (
    <Drawer
      visible={addingLocation}
      width={"80vw"}
      title={
        <Text style={{ fontWeight: "800",
          fontSize: 18 }}>
          + Agregar Franquicia
        </Text>
      }
      onClose={onClosed}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={16}>
          <Col span={13}>
            <Card
              style={{
                boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
                borderRadius: "15px",
              }}
            >
              <Text style={{ fontSize: 15 }} strong>
                Nombre de Franquicia <Text style={{ color: "red" }}>*</Text>
              </Text>
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingrese el nombre de la franquicia",
                  },
                ]}
              >
                <Input size="large" placeholder="Nombre de la franquicia" />
              </Form.Item>
              <Text style={{ fontSize: 15 }} strong>
                Teléfono <Text style={{ color: "red" }}>*</Text>
              </Text>
              <Form.Item
                name="phone"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingrese el teléfono de la franquicia",
                  },
                ]}
              >
                <Input size="large" placeholder="Teléfono de la franquicia" />
              </Form.Item>

              <Text style={{ fontSize: 15 }} strong>
                Subzona <Text style={{ color: "red" }}>*</Text>
              </Text>
              <Form.Item
                name="subZone"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingrese la subzona",
                  },
                ]}
              >
                <Cascader
                  size="large"
                  options={data?.zones.results.map(
                    (zone) => ({
                      value: zone.name,
                      label: zone.name,
                      children: zone.subZones.map((subZone) => ({
                        value: subZone.id,
                        label: subZone.name,
                      })),
                    }),
                  ) || []}
                  onChange={onChange}
                  placeholder="Selecciona la subzona" />
              </Form.Item>

              <Text style={{ fontSize: 15 }} strong>
                Dirección Completa <Text style={{ color: "red" }}>*</Text>
              </Text>
              <LocationSearchInput
                placeholder="Ingresa la dirección completa para buscarla..."
                value={locationSearch}
                onChange={(value) => {
                  setLocationSearch(value);
                }}
                onSelect={async (location) => {
                  if (!location) {
                    setLocationSearch("");
                    setDestination(baseAddress);
                  } else {
                    try {
                      const locationData = await formatAddress(location);
                      setDestination(locationData.destination);
                      setLocationSearch(
                        locationData.destination.fullName || "",
                      );

                      form.setFieldsValue({
                        address: {
                          fullName: locationData.destination.fullName,
                          street: locationData.destination.street,
                          colony: locationData.destination.colony,
                          postcode: locationData.destination.postcode,
                          city: locationData.destination.city,
                          country: locationData.destination.country,
                          state: locationData.destination.state,
                          exteriorNumber:
                            locationData.destination.exteriorNumber,
                          location: {
                            type: "Point",
                            coordinates:
                              locationData.destination.location?.coordinates ??
                              [],
                          },
                        },
                      });
                    } catch (error) {
                      console.error("Error formatting address:", error);
                    }
                  }
                }}
              />
              <Form.Item name={["address", "fullName"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "street"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "colony"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "postcode"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "city"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "state"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "country"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "exteriorNumber"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "location", "type"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={["address", "location", "coordinates"]} hidden>
                <Input />
              </Form.Item>

              <Form.Item>
                <Badge.Ribbon
                  text={
                    <Tooltip
                      autoAdjustOverflow
                      placement="topRight"
                      title="Ingresa direcciones completas para asegurar que tu ubicación es correcta."
                    >
                      <QuestionCircleOutlined />
                    </Tooltip>
                  }
                  placement="start"
                >
                  <Row justify="center" style={{ textAlign: "center" }}>
                    <Col span={6}>
                      <Text strong>País</Text>
                    </Col>
                    <Col span={6}>
                      <Text strong>Estado</Text>
                    </Col>
                    <Col span={6}>
                      <Text strong>Ciudad</Text>
                    </Col>
                    <Col span={6}>
                      <Text strong>Código postal</Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">{destination.country ?? "-"}</Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">{destination.state ?? "-"}</Text>
                    </Col>
                    <Col span={6}>
                      <Text type="secondary">{destination.city ?? "-"}</Text>
                    </Col>
                    <Col span={6} data-tut="zip_code">
                      <Text type="secondary">
                        {destination.postcode ?? "-"}
                      </Text>
                    </Col>
                  </Row>
                </Badge.Ribbon>
              </Form.Item>

              <Form.Item
                name={["address", "colony"]}
                rules={[{ required: true,
                  message: "Colonia es requerida" }]}
              >
                <Input size="large" placeholder="Colonia" />
              </Form.Item>

              <Form.Item
                name={["address", "street"]}
                rules={[{ required: true,
                  message: "Calle es requerida" }]}
              >
                <Input size="large" placeholder="Calle(s) y número" />
              </Form.Item>
            </Card>
          </Col>

          <Col span={11}>
            <Card
              style={{
                overflow: "hidden",
                boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
                borderRadius: "15px",
              }}
              bodyStyle={{
                paddingLeft: "0",
                paddingRight: "0",
                paddingBottom: "0",
                paddingTop: "0",
              }}
            >
              <Map
                style={{ height: "38vh" }}
                zoom={10}
                addDestinationInfo={() => {}}
              >
                {destination?.location?.coordinates?.length && (
                  <CustomMarker
                    key={destination?.location?.coordinates.join("-")}
                    lng={destination?.location?.coordinates[0]}
                    lat={destination?.location?.coordinates[1]}
                    src={"public/logo-pollo-loco.png"}
                    title="Franquicia"
                    onClick={() => {}}
                  />
                )}
              </Map>
            </Card>
            <Card
              style={{
                boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
                borderRadius: "15px",
                marginTop: 15,
              }}
              bodyStyle={{
                paddingBottom: "0",
              }}
            >
              <div>
                <Text style={{ fontSize: 15,
                  marginRight: 10 }} strong>
                  {imageUrl ? " Cambiar Imagen" : "Imagen"}:
                </Text>
                <Upload
                  beforeUpload={() => false}
                  listType="picture"
                  maxCount={1}
                  accept="image/*"
                  onChange={(info) => {
                    handleFileUpload(info);
                  }}
                  showUploadList={false}
                >
                  <Button style={{ marginLeft: 10 }} icon={<UploadOutlined />}>Seleccionar Imagen</Button>
                  {uploadLoding ? <Spin style={{ margin: 10 }} /> : null}
                </Upload>
                {imageUrl && (
                  <Image style={{ marginLeft: 20 }} width={70} src={imageUrl} />
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                <Text style={{ fontSize: 15 }} strong>
                  Instrucciones de Recoleción
                </Text>
                <Form.Item
                  name="collectionInstructions"
                >
                  <TextArea size="large" placeholder="Agregar instrucciones de recolección" />
                </Form.Item>
              </div>
            </Card>
          </Col>
        </Row>
      </Form>
      <Card
        title={<div>
          <Title level={4}>Asignar Usuarios</Title>
          <span>Se debe asignar al menos un tipo de usuario de Operador, Gerente y Repartidor.</span>
        </div>}
        style={{
          overflow: "hidden",
          boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
          borderRadius: "15px",
          marginTop: 15,
        }}
      >
        <UserSelectionForm
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
        />
      </Card>
      <Row justify="end" style={{ marginTop: "20px" }}>
        <Button
          style={{ width: "100%" }}
          disabled={
            !form.getFieldValue("name") ||
                    subZone === "" ||
                    !form.getFieldValue(["address", "fullName"]) ||
                     !Object.values(selectedUsers).every((userIds) =>
                       userIds.every((id) => id !== ""))
          }
          size="large"
          onClick={() => form.submit()}
          type="primary"
        >
          Crear Franquicia
        </Button>
      </Row>
    </Drawer>
  );
};

export default CreateLocationForm;
